/**
 * PNG Mask Processing Utilities
 *
 * Handles PNG-based masking instead of SVG path-based masking.
 * This approach is simpler and more reliable than SVG path parsing.
 */

import { getElementDimensions, validateDimensions } from './dimensionUtils';
import { supportsWebP } from '../../../utilities/webpDetection';

const imageType = supportsWebP() ? 'image/webp' : 'image/png';

export interface PNGMaskPositioning {
  maskCenterX: number;
  maskCenterY: number;
  maskScalePercent: number;
}

export interface PNGClippingResult {
  success: boolean;
  imageScale?: number;
  maskWidth?: number;
  maskHeight?: number;
}

// Simple memoization for positioning calculations
const positioningMemo = new Map<string, PNGMaskPositioning>();

/**
 * Clear positioning memoization cache
 */
export function clearPositioningCache(): void {
  positioningMemo.clear();
}

/**
 * Calculate mask positioning based on container dimensions and positioning config
 * x/y coordinates are percentages from top-left and represent the center of the mask
 */
export function calculatePNGMaskPositioning(options: {
  containerWidth: number;
  containerHeight: number;
  positioning?: { x: number; y: number; scale: number; rotation: number };
}): PNGMaskPositioning {
  const { containerWidth, containerHeight, positioning } = options;

  // Create cache key based on inputs
  const cacheKey = `${containerWidth}x${containerHeight}-${positioning?.x || 50}-${positioning?.y || 50}-${positioning?.scale || 100}`;

  // Check if we have a cached result
  if (positioningMemo.has(cacheKey)) {
    return positioningMemo.get(cacheKey)!;
  }

  // Use actual container dimensions, with fallback to reasonable defaults
  const safeWidth = containerWidth > 0 ? containerWidth : 800;
  const safeHeight = containerHeight > 0 ? containerHeight : 800;

  // Use positioning coordinates from the slot
  // x/y are percentages from top-left, representing the center of the mask
  const defaultX = 50; // 50% from left (center)
  const defaultY = 50; // 50% from top (center)
  const defaultScale = 100; // 100% of container width to cover the entire element

  // Use positioning coordinates if provided, otherwise use defaults
  const maskLeft = positioning?.x ?? defaultX;
  const maskTop = positioning?.y ?? defaultY;
  const scaleFromConfig = positioning?.scale ?? defaultScale;

  // Calculate center position using the provided coordinates
  // These coordinates represent the center of the mask
  let maskCenterX = (safeWidth / 100) * maskLeft;
  let maskCenterY = (safeHeight / 100) * maskTop;

  // If no positioning is provided, center the mask to cover the entire element
  if (!positioning) {
    maskCenterX = safeWidth / 2;
    maskCenterY = safeHeight / 2;
  }

  // Calculate scale percentage (convert from 0-100 to 0-1)
  const maskScalePercent = scaleFromConfig / 100;

  const result = {
    maskCenterX,
    maskCenterY,
    maskScalePercent
  };

  // Cache the result
  positioningMemo.set(cacheKey, result);

  return result;
}

/**
 * Load PNG mask image from raw binary data (ArrayBuffer or Uint8Array)
 * This is the preferred method for handling binary image data
 */
// export function loadPNGMaskFromBinary(binaryData: ArrayBuffer | Uint8Array): Promise<HTMLImageElement> {
//   return new Promise(async (resolve, reject) => {
//     try {
//       // Create a blob from the binary data
//       const blob = new Blob([binaryData as BlobPart], { type: 'image/png' });

//       // Convert blob to base64
//       const base64Data = await blobToBase64(blob);

//       // Create image with base64 data
//       const img = new Image();
//       // Set up event handlers BEFORE setting src
//       img.onload = () => {
//         resolve(img);
//       };

//       img.onerror = (error) => {
//         console.error('❌ Failed to load PNG mask from binary data:', error);
//         reject(new Error('Failed to load PNG mask from binary data'));
//       };

//       // Set crossOrigin for CORS handling
//       img.crossOrigin = 'anonymous';

//       // Set the source AFTER setting up event handlers
//       img.src = base64Data;
//     } catch (error) {
//       console.error('❌ Failed to process binary data:', error);
//       reject(new Error('Failed to process binary data'));
//     }
//   });
// }

/**
 * Load PNG mask image and return as HTMLImageElement
 * Handles URLs, data URLs, and raw binary data
 */
// function blobToBase64(blob: Blob): Promise<string> {
//   return new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.onloadend = () => resolve(reader.result as string);
//     reader.onerror = () => reject(new Error('Failed to convert blob to base64'));
//     reader.readAsDataURL(blob);
//   });
// }

export function loadPNGMask(options: { maskSvgBlob: Blob }): Promise<HTMLImageElement> {
  const { maskSvgBlob } = options;

  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();

    // Set up event handlers BEFORE setting src
    img.onload = () => {
      resolve(img);
    };

    img.onerror = (error) => {
      console.error('❌ Failed to load PNG mask from URL:', error);
      console.error('❌ Image source was:', maskSvgBlob.toString().substring(0, 100) + '...');
      reject(new Error('Failed to load PNG mask from URL'));
    };

    // Set crossOrigin for CORS handling
    img.crossOrigin = 'anonymous';

    // Load blob into image
    const blobUrl = URL.createObjectURL(maskSvgBlob);
    img.src = blobUrl;
  });
}

/**
 * Apply PNG-based clipping to canvas context using black shape with alpha background
 * The mask image should have a black shape with transparent background
 */
export function applyPNGClipping(options: {
  ctx: CanvasRenderingContext2D;
  maskImage: HTMLImageElement;
  positioning: PNGMaskPositioning;
  debugMode?: boolean;
}): PNGClippingResult {
  const { ctx, maskImage, positioning, debugMode = false } = options;
  try {
    // Calculate mask dimensions based on positioning
    const actualWidth = ctx.canvas.width;
    const maskAreaWidth = actualWidth * positioning.maskScalePercent;

    // Calculate aspect ratio from the mask image
    const maskAspectRatio = maskImage.height / maskImage.width;
    const maskAreaHeight = maskAreaWidth * maskAspectRatio;

    // Don't apply the mask yet - just set up the context for later use
    // The mask will be applied after the user's image is drawn
    // No context transformations needed here since applyPNGMaskClipping will handle positioning

    return {
      success: true,
      imageScale: 1,
      maskWidth: maskAreaWidth,
      maskHeight: maskAreaHeight
    };
  } catch (error) {
    console.error('❌ Error applying PNG clipping:', error);
    ctx.restore(); // Restore context state if there was an error
    throw error;
  }
}

/**
 * Apply the mask to clip the user's image after it has been drawn
 * This should be called AFTER the user's image is drawn to the canvas
 */
export function applyPNGMaskClipping(options: {
  ctx: CanvasRenderingContext2D;
  maskImage: HTMLImageElement;
  positioning: PNGMaskPositioning;
}): void {
  const { ctx, maskImage, positioning } = options;
  try {
    // Calculate mask dimensions based on positioning
    const actualWidth = ctx.canvas.width;
    const maskAreaWidth = actualWidth * positioning.maskScalePercent;

    // Calculate aspect ratio from the mask image
    const maskAspectRatio = maskImage.height / maskImage.width;
    const maskAreaHeight = maskAreaWidth * maskAspectRatio;

    // Calculate mask position (center the mask)
    const maskAreaX = positioning.maskCenterX - maskAreaWidth / 2;
    const maskAreaY = positioning.maskCenterY - maskAreaHeight / 2;

    // Apply the mask to clip the existing content (user's image)
    ctx.save();
    ctx.translate(maskAreaX, maskAreaY);
    ctx.scale(maskAreaWidth / maskImage.width, maskAreaHeight / maskImage.height);

    // Use destination-in to create a cut-out effect
    // Black areas in the mask (with alpha) will act as a "window" showing the user's image
    // Transparent areas in the mask will hide the user's image
    ctx.globalCompositeOperation = 'destination-in';
    ctx.drawImage(maskImage, 0, 0);
    ctx.restore();
  } catch (error) {
    console.error('❌ Error applying PNG mask clipping:', error);
    ctx.restore(); // Restore context state if there was an error
    throw error;
  }
}

/**
 * Apply PNG mask to an HTML element using CSS mask property
 * The mask should have a black shape with transparent background
 */
export async function applyPNGMaskToElement(
  element: HTMLElement,
  maskImageData: string | ArrayBuffer | Uint8Array,
  positioning: PNGMaskPositioning,
  maskImageConfig: { url: string; width: number; height: number }
): Promise<(() => void) | null> {
  try {
    // Get element dimensions using shared utility
    const dimensions = getElementDimensions(element);

    // Validate dimensions are reasonable
    if (!validateDimensions(dimensions)) {
      console.warn('PNG mask application failed: invalid element dimensions', dimensions);
      return null;
    }

    const safeWidth = dimensions.width;
    const safeHeight = dimensions.height;

    // Calculate mask dimensions based on container width (matching canvas and visualization logic)
    // maskScalePercent is a percentage of the container width, not the mask image width
    const maskAreaWidth = safeWidth * positioning.maskScalePercent;
    // Calculate height based on mask's aspect ratio
    const maskAspectRatio = maskImageConfig.height / maskImageConfig.width;
    const maskAreaHeight = maskAreaWidth * maskAspectRatio;

    // Calculate mask position with Safari-specific adjustments
    const maskAreaX = positioning.maskCenterX - maskAreaWidth / 2;
    const maskAreaY = positioning.maskCenterY - maskAreaHeight / 2;

    // Handle different data types using cached blob URLs
    let maskUrl: string;
    let isCachedUrl = false;

    if (maskImageData instanceof ArrayBuffer || maskImageData instanceof Uint8Array) {
      // Create blob URL for binary data
      const blob = new Blob([maskImageData as BlobPart], { type: imageType });
      maskUrl = URL.createObjectURL(blob);
      isCachedUrl = true;
    } else {
      // String data (URL or data URL) - use directly
      maskUrl = maskImageData;
    }

    // Apply positioning with Safari-specific adjustments
    const roundedMaskX = Math.round(maskAreaX);
    const roundedMaskY = Math.round(maskAreaY);

    console.log({ maskUrl, maskAreaWidth, maskAreaHeight, roundedMaskX, roundedMaskY });

    // Apply CSS mask properties with !important to ensure they override any existing styles
    // The mask image should have black areas (opaque) and transparent areas
    element.style.setProperty('mask-image', `url(${maskUrl})`, 'important');
    element.style.setProperty('-webkit-mask-image', `url(${maskUrl})`, 'important');
    element.style.setProperty(
      'mask-size',
      `${Math.round(maskAreaWidth)}px ${Math.round(maskAreaHeight)}px`,
      'important'
    );
    element.style.setProperty(
      '-webkit-mask-size',
      `${Math.round(maskAreaWidth)}px ${Math.round(maskAreaHeight)}px`,
      'important'
    );
    element.style.setProperty('mask-position', `${roundedMaskX}px ${roundedMaskY}px`, 'important');
    element.style.setProperty('-webkit-mask-position', `${roundedMaskX}px ${roundedMaskY}px`, 'important');
    element.style.setProperty('mask-repeat', 'no-repeat', 'important');
    element.style.setProperty('-webkit-mask-repeat', 'no-repeat', 'important');
    element.style.setProperty('mask-mode', 'alpha', 'important');

    // Also set the shorthand property
    element.style.setProperty(
      '-webkit-mask',
      `url(${maskUrl}) ${roundedMaskX}px ${roundedMaskY}px / ${Math.round(maskAreaWidth)}px ${Math.round(maskAreaHeight)}px no-repeat`,
      'important'
    );

    // Return enhanced cleanup function with better error handling
    return () => {
      try {
        // Clean up cached blob URL if we used one
        if (isCachedUrl) {
          URL.revokeObjectURL(maskUrl);
        }

        // Reset mask properties with fallback
        const resetMaskProperties = () => {
          element.style.setProperty('mask-image', 'none', 'important');
          element.style.setProperty('-webkit-mask-image', 'none', 'important');
          element.style.setProperty('mask-size', 'auto', 'important');
          element.style.setProperty('-webkit-mask-size', 'auto', 'important');
          element.style.setProperty('mask-position', '0px 0px', 'important');
          element.style.setProperty('-webkit-mask-position', '0px 0px', 'important');
          element.style.setProperty('mask-repeat', 'repeat', 'important');
          element.style.setProperty('-webkit-mask-repeat', 'repeat', 'important');
          element.style.setProperty('mask-mode', 'alpha', 'important');
          element.style.setProperty('-webkit-mask', 'none', 'important');
        };

        // Try to reset properties, with fallback to direct style manipulation
        try {
          resetMaskProperties();
        } catch (error) {
          console.warn('Failed to reset mask properties via setProperty, using direct assignment:', error);
          // Fallback: direct style manipulation
          element.style.maskImage = 'none';
          element.style.webkitMaskImage = 'none';
          element.style.maskSize = 'auto';
          element.style.webkitMaskSize = 'auto';
          element.style.maskPosition = '0px 0px';
          element.style.webkitMaskPosition = '0px 0px';
          element.style.maskRepeat = 'repeat';
          element.style.webkitMaskRepeat = 'repeat';
          element.style.maskMode = 'alpha';
          element.style.webkitMask = 'none';
        }
      } catch (error) {
        console.warn('Error during mask cleanup:', error);
      }
    };
  } catch {
    return null;
  }
}
